import React, { useState, useEffect } from 'react';
import { Container, CssBaseline, Box, Snackbar, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AppBar from './components/AppBar';
import ImageUploader from './components/ImageUploader';
import ImageList from './components/ImageList';
import ClassifyButton from './components/ClassifyButton';
import { getFiles, clearUploads, classifyImage, reloadSamples } from './services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#e41b08',
    },
    secondary: {
      // teal
      main: '#008080',
    },
  },
});

function App() {
  const [files, setFiles] = useState([]);
  const [classificationResults, setClassificationResults] = useState({});
  const [classifyingFiles, setClassifyingFiles] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await getFiles();
      console.log('Files:', response.data.files);
      setFiles(response.data.files ?? []);
    } catch (error) {
      console.error('Error fetching files:', error);
      showSnackbar('Error fetching files');
    }
  };

  const handleUpload = async (uploadedFiles) => {
    await fetchFiles();
  };

  const handleClassify = async (filename) => {
    setClassifyingFiles(prev => ({ ...prev, [filename]: true }));
    try {
      const response = await classifyImage(filename);
      setClassificationResults(prevResults => ({
        ...prevResults,
        [filename]: response.data
      }));
      showSnackbar(`Classification for ${filename} complete`);
    } catch (error) {
      console.error('Error classifying image:', error);
      showSnackbar(`Error classifying ${filename}`);
    } finally {
      setClassifyingFiles(prev => ({ ...prev, [filename]: false }));
    }
  };

  const handleClearAll = async () => {
    try {
      await clearUploads();
      setFiles([]);
      setClassificationResults({});
      showSnackbar('All uploads cleared');
    } catch (error) {
      console.error('Error clearing uploads:', error);
      showSnackbar('Error clearing uploads');
    }
  };

  const handleReloadSamples = async () => {
    try {
      await reloadSamples();
      await fetchFiles(); // Refetch the file list after reloading samples
      setClassificationResults({}); // Clear previous classification results
      showSnackbar('Sample images reloaded successfully');
    } catch (error) {
      console.error('Error reloading samples:', error);
      showSnackbar('Error reloading sample images');
    }
  };

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar />
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Stack direction={['column', 'row']} spacing={1} sx={{mb: 2}} useFlexGap>
            <ImageUploader onUpload={handleUpload} />
            <ClassifyButton onClearAll={handleClearAll} onReloadSamples={handleReloadSamples} />
          </Stack>
          {(!files || files.length === 0) && (
            <Box mt={4}>
              <Typography variant="subtitle1" color="text.secondary">
                请上传图片或加载测试图片
              </Typography>
            </Box>
          ) || (
            <ImageList 
              files={files} 
              onClassify={handleClassify} 
              classificationResults={classificationResults}
              classifyingFiles={classifyingFiles}
            />
          )}
        </Box>
      </Container>
      <Snackbar
        open={false && snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </ThemeProvider>
  );
}

export default App;